import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { searchFipThunk } from "../features/fip/fipThunk";
import { selectFip } from "../features/fip/fipSlice";
import Input from "../components/ui/Input";

const UserBank: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { fips, loading, error, selectedCategoryId } = useAppSelector((s) => s.fip);
    const [searchTerm, setSearchTerm] = React.useState("");

    useEffect(() => {
        // Only trigger FIP search on mount if we don't have results or if category changed
        dispatch(searchFipThunk({ assetClassId: selectedCategoryId || "BANK" }));
    }, [dispatch, selectedCategoryId]);

    // Debug: Log fips structure
    useEffect(() => {
        if (fips.length > 0) {
            console.log("FIPs loaded:", fips);
        }
    }, [fips]);

    // Filter FIPs locally based on search term
    const filteredFips = (fips || []).filter(fip => {
        const name = fip.FIP_NAME || "";
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="min-h-screen bg-fin-bg p-6">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-white">Select Your Bank</h1>
                    <p className="mt-1 text-fin-slate">Search for the bank where you hold your accounts</p>
                </div>

                <div className="mb-6">
                    <Input
                        placeholder="Search for your bank (e.g. HDFC, SBI)"
                        leftIcon={
                            <svg className="h-5 w-5 text-fin-slate" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        }
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>


                {loading && (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fin-primary"></div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-900/10 border border-red-900/20 text-red-400 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {!loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {filteredFips.map((fip) => (
                            <button
                                key={fip.FIP_ID}
                                onClick={() => {
                                    dispatch(selectFip(fip));
                                    navigate("/discoveryPrompt");
                                }}
                                className="flex items-center p-4 bg-fin-surface border border-fin-border rounded-xl hover:border-fin-primary hover:bg-fin-primary/5 transition-all duration-200 text-left group"
                            >
                                <div className="h-10 w-10 flex-shrink-0 rounded-full bg-fin-bg flex items-center justify-center p-1 border border-fin-border group-hover:border-fin-primary/30">
                                    {fip.LOGO_URL ? (
                                        <img src={fip.LOGO_URL} alt={fip.FIP_NAME} className="h-full w-full object-contain" />
                                    ) : (
                                        <span className="text-xs font-bold text-fin-slate">{fip.FIP_NAME.substring(0, 2)}</span>
                                    )}
                                </div>
                                <span className="ml-3 font-medium text-fin-slate group-hover:text-white">
                                    {fip.FIP_NAME}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {!loading && filteredFips.length === 0 && (
                    <div className="text-center py-12 bg-fin-surface rounded-xl border border-dashed border-fin-border">
                        <svg className="mx-auto h-12 w-12 text-fin-slate" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-white">No banks found</h3>
                        <p className="mt-1 text-sm text-fin-slate">Try checking for typos or using a different keyword.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserBank;
